import type { PayloadAction } from '@reduxjs/toolkit';
import type { SagaReturnType } from 'redux-saga/effects';
import { call, cancel, select, takeLatest } from 'redux-saga/effects';

import { getItem, setItem } from '@lt/components/helpers/storage';
import { storageKeys } from '@lt/widget/components/Cigarette/constants';
import { dataLayerPush } from 'src/helpers/gtm';
import type { SearchType } from 'src/types/search';

import type { postStatsExperimentRequest } from '@lt/api/dist/methods/stats/types';
import { getRecommenderTypeForSearchPages } from 'src/utils/featureToggleSigma/analytics/abNewRecommender';

import { APPLICATION_VERSION } from 'src/error/config/sentry/runTimeConfig';
import { getUserId } from 'src/utils/getUserId';
import { isSSR } from 'src/helpers/ssr';
import { api } from '../../api';
import { getClient, getClientStatsData } from '../client/selectors';
import type { initAppAction } from '../view/actions';
import { INIT_APP } from '../view/constants';
import {
  getAppName,
  getIsHotelOnly,
  getIsMobile,
  searchTypeSelector,
} from '../view/selectors';
import { sendPageviewAction } from './actions';
import { SEND_PAGEVIEW, SWITCH_TAB } from './constants';

import type { IStatsClient } from './types';

const GEO_KEY = 'lt-geoposition';

/**
 * 1. Пытаемся получить координаты в браузере
 * 2. Дергаем getReferencesClientCity чтобы получить город
 * 3. Сохраняем в localstorage, чтобы lt-tracker смог обработать значение
 *
 * @see https://github.com/LevelTravel/lt-tracker/blob/master/src/index.js#L229-L243
 */
function* fetchUserPosition() {
  const mock = {
    city: {
      name_en: '',
    },
    location: {
      lat: null,
      long: null,
    },
  };
  try {
    const data = localStorage.getItem(storageKeys.departure);
    if (data) {
      const departure = JSON.parse(data);
      const { success, ...geoData } = yield api.getReferencesClientCity({});

      if (success) {
        setItem({ key: GEO_KEY, payload: geoData });
      } else {
        setItem({
          key: GEO_KEY,
          payload: {
            city: {
              name_en: departure?.value ?? '',
            },
            location: {
              lat: null,
              long: null,
            },
          },
        });
      }
    } else {
      setItem({ key: GEO_KEY, payload: mock, ttl: 3 });
    }
  } catch (error) {
    // если геоданные получить не удалось, сохраняем пустое значение на 3 дня
    setItem({ key: GEO_KEY, payload: mock, ttl: 3 });
  }
}

/**
 * Запрашиваем геопозицию пользователя
 * Если данные сохранены в хранилище, то ручку не дергаем */
function* handleInitApp() {
  const data = getItem({ key: GEO_KEY });
  if (data?.location?.lat && data?.location?.long) {
    return;
  }

  yield fetchUserPosition();
}

/**
 * Сбор аналитики по переключению табов
 */
function handleSwitchSearchTab(
  action: PayloadAction<{
    previousSearchType: SearchType;
    searchType: SearchType;
    cigaretteType: string;
    url: string;
    isMobile: boolean;
  }>,
) {
  const { previousSearchType, searchType, url, isMobile, cigaretteType } =
    action.payload;
  const searchTypeStr = `${previousSearchType} to ${searchType}`;

  dataLayerPush({
    event: 'switch_tab',
    switch_tab_params: {
      search_type: searchTypeStr,
      cigarette_type: cigaretteType,
      url,
      mobile: isMobile,
    },
  });
}

function* getCurrentModuleName() {
  const page: SagaReturnType<typeof getAppName> = yield select(getAppName);
  const isHotelOnly: SagaReturnType<typeof getIsHotelOnly> = yield select(
    getIsHotelOnly,
  );
  /**
   * На туровом чекауте нужно отправлять не страницу, а шаг чекаута
   * Отель и поиск разделяем на туровый и отельный
   */
  switch (page) {
    case 'package_checkout':
      return 'package_checkout_flights';
    case 'hotel':
      return isHotelOnly ? 'hotel_landing_hotels_only' : 'hotel_landing_tours';
    case 'search':
      return isHotelOnly ? 'search_hotels' : 'search_tours';
    default:
      return page;
  }
}

export type StepNamePage = Exclude<
  SagaReturnType<typeof getCurrentModuleName>,
  | 'confirm_email'
  | 'cancellation_policy'
  | 'warn_debt'
  | 'order_changing_request'
  | 'main_order'
  | 'pay_card'
  | 'modern_mobile_home'
  | 'modern_desktop_home'
  | 'client'
>;

export function pushTBankPixel() {
  try {
    // не должен тбанк пиксель улетать на альфе
    if (!isSSR && window.PARTNER?.id === 1220) {
      return;
    }
    // игнорируем ошибку ts, так как TidSDK подключается в head через _head_tbank_pixel.html.erb и _head_scripts.html.haml
    // @ts-ignore: error message
    const tidSdk = new TidSDK({
      clientId: window.LTSocialAuthConfig.tbank.client_id,
    });
    tidSdk.addPixel();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

// Отправляем pageview для каждой страницы
export function* sendPageviewAnalytics(
  action:
    | ReturnType<typeof sendPageviewAction>
    | ReturnType<typeof initAppAction>,
) {
  try {
    const isClientStoreAvailable = Boolean(yield select(getClient));
    const clientData: SagaReturnType<typeof getClientStatsData> =
      isClientStoreAvailable
        ? yield select(getClientStatsData)
        : { uuid: getUserId() };

    const client: IStatsClient = {
      id: clientData.id || undefined,
      email: clientData.email || undefined,
      phone: clientData.phone || undefined,
      uuid: getUserId(),
    };
    const isMobile = yield select(getIsMobile);
    const { referrer } = document;
    const partnerId = window.PARTNER ? window.PARTNER.id : null;
    const stepName: StepNamePage =
      action.type === SEND_PAGEVIEW
        ? action.payload
        : yield call(getCurrentModuleName);
    if (!stepName) return;
    if (!APPLICATION_VERSION)
      throw new Error('No application version provided!');

    yield api.postStatsPageview({
      client,
      is_mobile: isMobile,
      referrer,
      partner_id: partnerId,
      step_name: stepName,
      release_version: APPLICATION_VERSION,
      url: window.location.href,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

function* handleFirstSession() {
  const FIRST_VISIT_KEY = 'has_visited_before';
  const hasVisited = yield call(getItem, { key: FIRST_VISIT_KEY });

  if (!hasVisited) {
    yield call(dataLayerPush, {
      event: 'first_session',
      eventAction: 'first_session',
    });
    yield call(setItem, {
      key: FIRST_VISIT_KEY,
      payload: true,
    });
  }
}

function* sendRecommenderABInfo() {
  const appName = yield call(getCurrentModuleName);
  const isMobile: SagaReturnType<typeof getIsMobile> = yield select(
    getIsMobile,
  );

  if (appName !== 'search_tours' || isMobile) yield cancel();

  const searchType: SagaReturnType<typeof searchTypeSelector> = yield select(
    searchTypeSelector,
  );
  const isClientStoreAvailable = Boolean(yield select(getClient));
  const clientData: SagaReturnType<typeof getClientStatsData> =
    isClientStoreAvailable
      ? yield select(getClientStatsData)
      : { uuid: getUserId() };

  const partnerId = window.PARTNER ? window.PARTNER.id : null;

  const { group } = yield call(
    getRecommenderTypeForSearchPages,
    false,
    'package',
  );

  const data: postStatsExperimentRequest = {
    search_type: searchType,
    url: window.location.href,
    client: clientData,
    is_mobile: isMobile,
    release_version: APPLICATION_VERSION ?? '',
    partner_id: partnerId,
    experiment: {
      key: 'ab_recommender',
      value: group,
    },
    event: 'search_tours',
  };

  try {
    yield api.postStatsExperiment(data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

export default function* analyticsSaga() {
  yield takeLatest(INIT_APP, handleFirstSession);
  yield takeLatest(INIT_APP, handleInitApp);
  yield takeLatest([INIT_APP, sendPageviewAction], sendPageviewAnalytics);
  yield takeLatest([INIT_APP, sendPageviewAction], pushTBankPixel); // нужно вызывать эту функцию тогда же, когда и sendPageviewAnalytics
  yield takeLatest(SWITCH_TAB, handleSwitchSearchTab);
  yield takeLatest(INIT_APP, sendRecommenderABInfo);
}
